<script setup>
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import AdminActionDialog from '../components/AdminActionDialog.vue'
import LifecycleStatusBadge from '../components/LifecycleStatusBadge.vue'
import { useOrgStore } from '../stores/orgStore.js'
import { useSubOrgStore } from '../stores/subOrgStore.js'
import { getAdminEmptyStateMessage, parseActionError } from '../utils/adminUi.js'
import { canModifyOrg, canModifySubOrg, canRestoreSubOrg } from '../utils/lifecycleUtils.js'

const orgStore = useOrgStore()
const subOrgStore = useSubOrgStore()
const { activeOrgId } = storeToRefs(orgStore)

const creating = ref(false)
const updating = ref(false)
const deletingId = ref(null)
const actionError = ref('')
const actionMessage = ref('')
const subOrgName = ref('')
const subOrgDescription = ref('')
const editingId = ref(null)
const editName = ref('')
const editDescription = ref('')
const restoringId = ref(null)
const pendingAction = ref(null)

const organizations = computed(() => orgStore.organizations ?? [])
const selectedOrgId = computed({
  get: () => (activeOrgId.value === null || activeOrgId.value === undefined ? null : Number(activeOrgId.value)),
  set: (value) => {
    if (value === null || value === undefined || value === '') {
      orgStore.setActiveOrg(null)
      return
    }
    orgStore.setActiveOrg(Number(value))
  },
})
const subOrgs = computed(() => subOrgStore.byOrg(activeOrgId.value))
const selectedOrganization = computed(() => organizations.value.find((org) => Number(org?.id) === Number(selectedOrgId.value)) ?? null)
const selectedOrgIsReadOnly = computed(() => !canModifyOrg(selectedOrganization.value))
const emptyStateMessage = computed(() => getAdminEmptyStateMessage('suborganizations', {
  hasOrganization: Boolean(orgStore.activeOrgId),
  parentDeleted: selectedOrgIsReadOnly.value,
}))
const pendingActionTitle = computed(() => {
  return pendingAction.value?.type === 'restore' ? 'Restore Suborganization' : 'Deactivate Suborganization'
})
const pendingActionMessage = computed(() => {
  if (pendingAction.value?.type === 'restore') {
    return 'This suborganization will become active again.'
  }

  return 'This suborganization will no longer be operational.'
})
const pendingActionDetails = computed(() => {
  if (pendingAction.value?.type === 'restore') {
    return ['Existing audit history will be preserved.']
  }

  return [
    'Deleted suborganizations remain read-only until restored.',
  ]
})
const pendingActionConfirmLabel = computed(() => {
  return pendingAction.value?.type === 'restore' ? 'Restore Suborganization' : 'Deactivate Suborganization'
})
const pendingActionLoadingLabel = computed(() => {
  return pendingAction.value?.type === 'restore' ? 'Restoring sub-organization...' : 'Deleting sub-organization...'
})
const pendingActionTone = computed(() => {
  return pendingAction.value?.type === 'restore' ? 'success' : 'danger'
})

const rowActionBusy = (subOrgId) => {
  return deletingId.value === subOrgId || restoringId.value === subOrgId || (editingId.value === subOrgId && updating.value)
}

const syncStaleSubOrgState = () => {
  const visibleIds = new Set(subOrgs.value.map((item) => Number(item?.id)).filter((id) => Number.isInteger(id) && id > 0))

  if (editingId.value !== null && !visibleIds.has(Number(editingId.value))) {
    cancelEdit()
  }

  if (pendingAction.value?.subOrg?.id && !visibleIds.has(Number(pendingAction.value.subOrg.id))) {
    pendingAction.value = null
  }
}

const parseError = (err, fallback) => {
  if (!err) return fallback
  if (typeof err === 'string') return err
  const detail = err?.response?.data?.detail
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail) && detail.length) {
    return detail.map((item) => item?.msg).filter(Boolean).join(', ') || fallback
  }
  return fallback
}


const loadSubOrgs = async (options = {}) => {
  if (!options.preserveFeedback) {
    actionError.value = ''
    actionMessage.value = ''
  }
  const result = await subOrgStore.fetchSubOrgs({ status: 'all' })
  if (!result.success) {
    actionError.value = parseError(result.error, 'Failed to load sub-organizations')
    if (!selectedOrganization.value) {
      cancelEdit()
      pendingAction.value = null
    }
    return
  }
  syncStaleSubOrgState()
}

const createSubOrg = async () => {
  actionError.value = ''
  actionMessage.value = ''
  const name = subOrgName.value.trim()
  if (!name) {
    actionError.value = 'Sub-organization name is required.'
    return
  }
  const organizationId = Number(selectedOrgId.value)
  if (!Number.isInteger(organizationId) || organizationId <= 0) {
    actionError.value = 'Organization must be selected.'
    return
  }
  if (selectedOrgIsReadOnly.value) {
    actionError.value = 'Cannot create sub-organization under a deleted organization.'
    return
  }

  creating.value = true
  try {
    const result = await subOrgStore.createSubOrg(name, subOrgDescription.value.trim(), organizationId)
    if (!result.success) {
      actionError.value = parseAdminError(result.error, 'Failed to create sub-organization')
      return
    }
    subOrgName.value = ''
    subOrgDescription.value = ''
    actionMessage.value = `Sub-organization "${result?.data?.name ?? name}" created successfully.`
    await loadSubOrgs({ preserveFeedback: true })
  } finally {
    creating.value = false
  }
}

const startEdit = (subOrg) => {
  if (!canModifySubOrg(subOrg)) {
    actionError.value = 'Deleted sub-organizations are read-only and cannot be edited.'
    return
  }
  editingId.value = subOrg.id
  editName.value = subOrg.name ?? ''
  editDescription.value = subOrg.description ?? ''
  actionError.value = ''
  actionMessage.value = ''
}

const cancelEdit = () => {
  editingId.value = null
  editName.value = ''
  editDescription.value = ''
}

const saveEdit = async (subOrgId) => {
  actionError.value = ''
  actionMessage.value = ''
  const name = editName.value.trim()
  if (!name) {
    actionError.value = 'Sub-organization name is required.'
    return
  }

  const target = subOrgs.value.find((item) => Number(item?.id) === Number(subOrgId))
  if (!canModifySubOrg(target)) {
    actionError.value = 'Deleted sub-organizations are read-only and cannot be edited.'
    return
  }

  updating.value = true
  try {
    const result = await subOrgStore.updateSubOrg(subOrgId, name, editDescription.value.trim())
    if (!result.success) {
      actionError.value = parseActionError(result.error, 'Failed to update sub-organization', {
        notFound: 'This sub-organization is no longer available. Refresh the list and try again.',
        forbidden: 'Deleted sub-organizations are read-only and cannot be edited.',
      })
      await loadSubOrgs({ preserveFeedback: true })
      return
    }
    actionMessage.value = `Sub-organization "${result?.data?.name ?? name}" updated successfully.`
    cancelEdit()
    await loadSubOrgs({ preserveFeedback: true })
  } finally {
    updating.value = false
  }
}

const openDeleteDialog = (subOrg) => {
  actionError.value = ''
  actionMessage.value = ''
  if (!canModifySubOrg(subOrg)) {
    actionError.value = 'Deleted sub-organizations are read-only and cannot be modified.'
    return
  }

  if (rowActionBusy(subOrg.id)) return

  pendingAction.value = {
    type: 'delete',
    subOrg,
  }
}

const openRestoreDialog = (subOrg) => {
  actionError.value = ''
  actionMessage.value = ''

  if (!canRestoreSubOrg(subOrg, selectedOrganization.value)) {
    actionError.value = 'Cannot restore sub-organization while parent organization is deleted.'
    return
  }

  if (rowActionBusy(subOrg.id)) return

  pendingAction.value = {
    type: 'restore',
    subOrg,
  }
}

const closePendingAction = () => {
  if (deletingId.value !== null || restoringId.value !== null) return
  pendingAction.value = null
}

const removeSubOrg = async (subOrg) => {
  if (!subOrg?.id || rowActionBusy(subOrg.id)) return
  actionError.value = ''
  actionMessage.value = ''

  deletingId.value = subOrg.id
  try {
    const result = await subOrgStore.deleteSubOrg(subOrg.id)
    if (!result.success) {
      actionError.value = parseActionError(result.error, 'Failed to delete sub-organization', {
        notFound: 'This sub-organization is no longer available. Refresh the list and try again.',
        forbidden: 'Deleted sub-organizations are read-only and cannot be deleted again.',
      })
      await loadSubOrgs({ preserveFeedback: true })
      return
    }
    actionMessage.value = `Suborganization deactivated successfully: ${subOrg.name}.`
    if (editingId.value === subOrg.id) {
      cancelEdit()
    }
    await loadSubOrgs({ preserveFeedback: true })
  } finally {
    deletingId.value = null
  }
}

const restoreSubOrg = async (subOrg) => {
  if (!subOrg?.id || rowActionBusy(subOrg.id)) return
  actionError.value = ''
  actionMessage.value = ''

  if (!canRestoreSubOrg(subOrg, selectedOrganization.value)) {
    actionError.value = 'Cannot restore sub-organization while parent organization is deleted.'
    return
  }

  restoringId.value = subOrg.id
  try {
    const result = await subOrgStore.restoreSubOrg(subOrg.id)
    if (!result.success) {
      actionError.value = parseActionError(result.error, 'Failed to restore sub-organization', {
        notFound: 'This sub-organization is no longer available. Refresh the list and try again.',
        forbidden: 'You no longer have permission to restore this sub-organization.',
        conflict: 'Cannot restore sub-organization while parent organization is deleted.',
      })
      await loadSubOrgs({ preserveFeedback: true })
      return
    }
    actionMessage.value = `Suborganization restored successfully: ${result?.data?.name ?? subOrg.name}.`
    await loadSubOrgs({ preserveFeedback: true })
  } finally {
    restoringId.value = null
  }
}

const confirmPendingAction = async () => {
  if (deletingId.value !== null || restoringId.value !== null) return
  const nextAction = pendingAction.value
  if (!nextAction?.subOrg) return

  if (nextAction.type === 'delete') {
    await removeSubOrg(nextAction.subOrg)
  }

  if (nextAction.type === 'restore') {
    await restoreSubOrg(nextAction.subOrg)
  }

  pendingAction.value = null
}

onMounted(async () => {
  if (!organizations.value.length) {
    await orgStore.fetchMyOrganizations()
  }

  if (activeOrgId.value !== null && activeOrgId.value !== undefined) {
    activeOrgId.value = Number(activeOrgId.value)
  } else if (organizations.value.length) {
    const preferredOrg = organizations.value.find((org) => org?.is_deleted !== true) ?? organizations.value[0]
    orgStore.setActiveOrg(Number(preferredOrg.id))
  }

  await loadSubOrgs()
})
</script>

<template>
  <div class="suborg-admin-panel">
    <h1>Sub-Organization Admin</h1>

    <section class="create-suborg">
      <h2>Create Sub-Organization</h2>
      <form @submit.prevent="createSubOrg" class="form-col">
        <select v-model="selectedOrgId">
          <option v-for="org in organizations" :key="org.id" :value="org.id">
            {{ org.name }}{{ org.is_deleted ? ' (deleted)' : '' }}
          </option>
        </select>
        <p v-if="selectedOrgIsReadOnly" class="warning">Selected organization is deleted. Create is disabled.</p>
        <input v-model="subOrgName" type="text" placeholder="Sub-organization name" :disabled="!orgStore.activeOrgId || selectedOrgIsReadOnly" />
        <textarea
          v-model="subOrgDescription"
          rows="2"
          placeholder="Description (optional)"
          :disabled="!orgStore.activeOrgId || selectedOrgIsReadOnly"
        />
        <button type="submit" :disabled="creating || !orgStore.activeOrgId || selectedOrgIsReadOnly">
          {{ creating ? 'Creating...' : 'Create Sub-Organization' }}
        </button>
      </form>
    </section>

    <section class="suborg-list">
      <h2>Sub-Organizations for Active Organization</h2>
      <button @click="loadSubOrgs" :disabled="subOrgStore.loading">{{ subOrgStore.loading ? 'Loading...' : 'Refresh' }}</button>
      <p v-if="!orgStore.activeOrgId" class="error">Select an organization first.</p>
      <p v-if="actionMessage" class="success">{{ actionMessage }}</p>
      <p v-if="actionError || subOrgStore.error" class="error">{{ actionError || subOrgStore.error }}</p>

      <ul v-if="subOrgs.length">
        <li v-for="subOrg in subOrgs" :key="subOrg.id" class="suborg-row">
          <template v-if="editingId === subOrg.id">
            <input v-model="editName" type="text" />
            <textarea v-model="editDescription" rows="2" />
            <div class="actions">
              <button @click="saveEdit(subOrg.id)" :disabled="updating">{{ updating ? 'Saving...' : 'Save' }}</button>
              <button class="secondary" @click="cancelEdit" type="button">Cancel</button>
            </div>
          </template>
          <template v-else>
            <div>
              <strong>#{{ subOrg.id }} - {{ subOrg.name }}</strong>
              <LifecycleStatusBadge :entity="subOrg" />
              <p>{{ subOrg.description || 'No description' }}</p>
              <p v-if="subOrg.is_deleted" class="warning">Read-only: deleted sub-organizations cannot be edited or deleted.</p>
              <p v-if="subOrg.is_deleted && selectedOrgIsReadOnly" class="warning">Restore is unavailable while the parent organization is deleted.</p>
            </div>
            <div class="actions">
              <button @click="startEdit(subOrg)" :disabled="rowActionBusy(subOrg.id) || !canModifySubOrg(subOrg)" type="button">Edit</button>
              <button
                class="danger"
                @click="openDeleteDialog(subOrg)"
                :disabled="rowActionBusy(subOrg.id) || !canModifySubOrg(subOrg)"
                :data-testid="`suborg-delete-${subOrg.id}`"
                type="button"
              >
                {{ deletingId === subOrg.id ? 'Deleting...' : 'Delete' }}
              </button>
              <button
                v-if="subOrg.is_deleted"
                class="restore"
                @click="openRestoreDialog(subOrg)"
                :disabled="rowActionBusy(subOrg.id) || !canRestoreSubOrg(subOrg, selectedOrganization)"
                :data-testid="`suborg-restore-${subOrg.id}`"
                type="button"
              >
                {{ restoringId === subOrg.id ? 'Restoring...' : 'Restore' }}
              </button>
            </div>
          </template>
        </li>
      </ul>
      <p v-else class="empty-state">{{ emptyStateMessage }}</p>
    </section>

    <AdminActionDialog
      :open="Boolean(pendingAction)"
      :title="pendingActionTitle"
      :message="pendingActionMessage"
      :details="pendingActionDetails"
      :confirm-label="pendingActionConfirmLabel"
      :loading-label="pendingActionLoadingLabel"
      :tone="pendingActionTone"
      :loading="deletingId !== null || restoringId !== null"
      @close="closePendingAction"
      @confirm="confirmPendingAction"
    />
  </div>
</template>

<style scoped>
.suborg-admin-panel {
  max-width: 960px;
  margin: 0 auto;
  padding: 2rem;
  display: grid;
  gap: 1.5rem;
}

h1,
h2 {
  color: #111827;
}

.form-col {
  display: grid;
  gap: 0.5rem;
  max-width: 520px;
}

input,
textarea {
  padding: 0.5rem;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
}

button {
  border: none;
  border-radius: 6px;
  background: #2563eb;
  color: #fff;
  padding: 0.5rem 0.85rem;
}

.secondary {
  background: #4b5563;
}

.restore {
  background: #047857;
}

.danger {
  background: #b91c1c;
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.suborg-row {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.75rem;
  display: grid;
  gap: 0.5rem;
}

.success {
  color: #166534;
}

.error {
  color: #b91c1c;
}

.warning {
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 0.35rem 0.5rem;
}

ul {
  list-style: none;
  padding: 0;
  margin: 0.75rem 0 0;
  display: grid;
  gap: 0.5rem;
}

p {
  margin: 0.25rem 0;
}

.empty-state {
  color: #6b7280;
}
</style>
